import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import _ from 'lodash';
import ContentConstants from '../contentConstants';
import { IDistributionContact } from '../models';
import * as DistributionContactAPI from '../services/DistributionContactAPI';
import { ActionTypes } from './ActionTypes';

interface IPendingParticipantsState {
	status: 'loading' | 'idle' | 'saving';
	pendingParticipants: IDistributionContact[];
}

const initialState: IPendingParticipantsState = {
	status: 'idle',
	pendingParticipants: [],
};

const loadPendingParticipants = createAsyncThunk('pendingParticipants/loadPendingParticipants', async (surveyId: number) => {
	return DistributionContactAPI.getPendingParticipants(surveyId);
});

const savePendingParticipants = createAsyncThunk(
	'pendingParticipants/savePendingParticipants',
	async (args: { surveyId: number; contacts: IDistributionContact[] }, { dispatch, rejectWithValue }) => {
		try {
			await DistributionContactAPI.savePendingParticipants(args.surveyId, args.contacts);
			dispatch({ type: ActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_ASSIGN_AUDIENCE_SUCCEEDED });
			return args.contacts.map((p) => p.distributionContactId);
		} catch (error: unknown) {
			let validationErrors;
			if (axios.isAxiosError(error) && error.response && error.response.data && error.response.data.validationErrors) {
				validationErrors = error.response.data.validationErrors.validations;
			}
			dispatch({
				type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
				payload: {
					message: validationErrors['AccessCodes'],
					messageTitle: ContentConstants.TOAST_ERROR_GENERIC_TITLE,
				},
			});
			rejectWithValue(error);
		}
	},
);

const pendingParticipantsSlice = createSlice({
	name: 'pendingParticipants',
	initialState: initialState,
	reducers: {
		updatePendingParticipants: (state, action: PayloadAction<{ contactId: number; accessCode: string }>) => {
			const {
				payload: { contactId, accessCode },
			} = action;
			const updatedParticipants: IDistributionContact[] = _.cloneDeep(state.pendingParticipants).map((p) =>
				p.distributionContactId === contactId ? { ...p, accessCode: accessCode } : p,
			);
			return {
				...state,
				pendingParticipants: updatedParticipants,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadPendingParticipants.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(loadPendingParticipants.fulfilled, (state, action) => {
				state.pendingParticipants = action.payload;
				state.status = 'idle';
			})
			.addCase(loadPendingParticipants.rejected, (state) => {
				state.status = 'idle';
			});
		builder.addCase(savePendingParticipants.fulfilled, (state, action) => {
			state.pendingParticipants =
				action.payload !== undefined
					? _.cloneDeep(state.pendingParticipants).filter((p) => !action.payload?.includes(p.distributionContactId))
					: state.pendingParticipants;
		});
	},
});

export { initialState, loadPendingParticipants, savePendingParticipants, type IPendingParticipantsState };
export const { updatePendingParticipants } = pendingParticipantsSlice.actions;
export default pendingParticipantsSlice.reducer;
